import _ from 'lodash';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import {
  BottomOverflowShadow,
  InfoTableComponent,
  LeftOverflowShadow,
  RightOverflowShadow,
  ScrollAreaContent,
  ScrollAreaScrollLeft,
  ScrollAreaScrollRight,
  TableWrap,
  TopOverflowShadow,
} from './styles';
import { InfoTableProps } from './types';

export const InfoTable: React.FC<InfoTableProps> = ({
  children,
  className,
  fixTopHeaders = false,
  fixLeftHeaders = false,
  showScrollButtons = false,
  noTopHeaders = false,
  noLeftHeaders = false,
}) => {
  const [leftOverflowed, setLeftOverflowed] = useState<boolean>(false);
  const [rightOverflowed, setRightOverflowed] = useState<boolean>(false);
  const [topOverflowed, setTopOverflowed] = useState<boolean>(false);
  const [bottomOverflowed, setBottomOverflowed] = useState<boolean>(false);
  const [verticallyScrolled, setVerticallyScrolled] = useState<boolean>(false);

  const scrollAreaEl = useRef<HTMLDivElement>(null);

  const onScroll = (): void => {
    const elem = scrollAreaEl?.current;

    if (!elem) return;

    const horizontalScrollable = elem.scrollWidth > elem.clientWidth;
    const contentOnLeft = elem.scrollLeft > 0;
    const contentOnRight =
      elem.scrollLeft < elem.scrollWidth - elem.clientWidth;

    const verticalScrollable = elem.scrollHeight > elem.clientHeight;
    const contentOnTop = elem.scrollTop > 0;
    const contentOnBottom =
      elem.scrollTop < elem.scrollHeight - elem.clientHeight;

    const verticallyScrolled = elem.scrollTop > 0;

    setLeftOverflowed(horizontalScrollable && contentOnLeft);
    setRightOverflowed(horizontalScrollable && contentOnRight);
    setTopOverflowed(verticalScrollable && contentOnTop);
    setBottomOverflowed(verticalScrollable && contentOnBottom);
    setVerticallyScrolled(verticalScrollable && verticallyScrolled);
  };

  const throttleOnScroll = _.throttle(onScroll, 100);

  useEffect(() => {
    onScroll();
    scrollAreaEl?.current?.addEventListener('scroll', throttleOnScroll);

    return () => {
      scrollAreaEl?.current?.removeEventListener('scroll', throttleOnScroll);
    };
  }, []);

  const IconArrowRight = (): ReactElement => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="none"
        fillRule="evenodd"
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M5.5.75l10.72 10.72v0c.29.29.29.76 0 1.05 -.01 0-.01 0-.01 0L5.49 23.24"
      />
    </svg>
  );

  const IconArrowLeft = (): ReactElement => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path
        fill="none"
        fillRule="evenodd"
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M16.25 23.25L5.53 12.53h-.01c-.3-.3-.3-.77-.01-1.06 0-.01 0-.01 0-.01L16.23.74"
      />
    </svg>
  );

  const handleScrollLeft = (): void => {
    const scrollArea = scrollAreaEl?.current;

    scrollArea?.scroll({
      top: scrollArea.scrollTop,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleScrollRight = (): void => {
    const scrollArea = scrollAreaEl?.current;

    scrollArea?.scroll({
      top: scrollArea.scrollTop,
      left: scrollArea.scrollWidth - scrollArea.clientWidth,
      behavior: 'smooth',
    });
  };

  const dataForStyle = {
    fixTopHeaders,
    fixLeftHeaders,
    leftOverflowed,
    rightOverflowed,
    topOverflowed,
    bottomOverflowed,
    verticallyScrolled,
    noTopHeaders,
    noLeftHeaders,
  };

  return (
    <InfoTableComponent className={className}>
      <TableWrap>
        <ScrollAreaContent ref={scrollAreaEl} dataForStyle={dataForStyle}>
          {children}

          {/* Shadows to indicate user can scroll to reveal overflow content */}
          {(noTopHeaders || !fixTopHeaders) && topOverflowed && (
            <TopOverflowShadow />
          )}
          {(noLeftHeaders || !fixLeftHeaders) && leftOverflowed && (
            <LeftOverflowShadow />
          )}
          {bottomOverflowed && <BottomOverflowShadow />}
          {rightOverflowed && <RightOverflowShadow />}
        </ScrollAreaContent>
      </TableWrap>
      {showScrollButtons && leftOverflowed && (
        <ScrollAreaScrollLeft type="button" onClick={handleScrollLeft}>
          <IconArrowLeft />
        </ScrollAreaScrollLeft>
      )}
      {showScrollButtons && rightOverflowed && (
        <ScrollAreaScrollRight type="button" onClick={handleScrollRight}>
          <IconArrowRight />
        </ScrollAreaScrollRight>
      )}
    </InfoTableComponent>
  );
};
